import { defineColumns } from './piColumns.js';
import { EMPTY, formatDateFull, formatInteger, formatPercent, isNumber } from './piFormat.js';
import { confidenceLabel, insightStatusLabel } from './piInsights.js';
import { listOf, objectOf } from './piRequests.js';
import { deltaAllowed, showsValue } from './piState.js';
import { guardedMetric, numberOf } from './piTable.js';

export const OVERVIEW_KPI_INFO = {
  dau: 'overview.dau',
  wau: 'engagement.wau',
  mau: 'engagement.mau',
  stickiness: 'overview.stickiness',
  activeCompanies: 'engagement.activeCompaniesMonth',
  activeSeatsRate: 'common.activeSeatsRate',
  was: 'engagement.was',
  activation30d: 'overview.activation30d',
  retentionW4: 'overview.retentionW4',
  top10Concentration: 'overview.concentration',
};

export const PREVIOUS_REASON = 'sem base de comparação';

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

export const indicatorValue = (key) => (row) => objectOf(objectOf(row).metrics)[key] || null;

export const indicatorCount = (key) => (row) => numberOf(objectOf(objectOf(row).counts)[key]);

export const periodLabel = (period, previous) => {
  const range = objectOf(period);
  const text = formatDateFull(range.from) + ' a ' + formatDateFull(range.to);
  return previous ? text + ' (período anterior)' : text;
};

export const previousMetric = (raw) => {
  const metric = guardedMetric(raw);
  if (deltaAllowed(metric)) return { value: metric.previous, unit: metric.unit, state: 'ok' };
  return { value: null, unit: metric.unit, state: 'na', stateReason: PREVIOUS_REASON };
};

export const indicatorRows = (items, options) => {
  const settings = objectOf(options);
  const current = {};
  const before = {};
  const counts = objectOf(settings.counts);
  listOf(items).forEach((item) => {
    const key = textOf(item.key);
    if (!key) return;
    const metric = item.metric && typeof item.metric === 'object' ? item.metric : null;
    current[key] = metric;
    before[key] = previousMetric(metric);
  });
  const rows = [{ period: periodLabel(settings.period, false), metrics: current, counts: counts }];
  if (settings.compare === true && settings.previous) rows.push({ period: periodLabel(settings.previous, true), metrics: before, counts: {} });
  return rows;
};

export const OVERVIEW_KPI_COLUMNS = defineColumns([
  { key: 'period', label: 'Período', type: 'text', infoKey: 'filter.period', screen: false },
  { key: 'dau', label: 'DAU (média do período)', type: 'metric', unit: 'count', infoKey: OVERVIEW_KPI_INFO.dau, value: indicatorValue('dau') },
  { key: 'wau', label: 'WAU', type: 'metric', unit: 'count', infoKey: OVERVIEW_KPI_INFO.wau, value: indicatorValue('wau') },
  { key: 'mau', label: 'MAU', type: 'metric', unit: 'count', infoKey: OVERVIEW_KPI_INFO.mau, value: indicatorValue('mau') },
  {
    key: 'stickiness', label: 'Stickiness (DAU/MAU)', type: 'metric', unit: 'pct', infoKey: OVERVIEW_KPI_INFO.stickiness, value: indicatorValue('stickiness'),
  },
  {
    key: 'activeCompanies', label: 'Empresas com uso real (30 dias)', type: 'metric', unit: 'count', infoKey: OVERVIEW_KPI_INFO.activeCompanies,
    value: indicatorValue('activeCompanies'),
  },
  {
    key: 'activeSeatsRate', label: 'Active Seats Rate (30 dias)', type: 'metric', unit: 'pct', infoKey: OVERVIEW_KPI_INFO.activeSeatsRate,
    value: indicatorValue('activeSeatsRate'),
  },
  { key: 'was', label: 'WAS (assentos em uso na semana)', type: 'metric', unit: 'pct', infoKey: OVERVIEW_KPI_INFO.was, value: indicatorValue('was') },
  {
    key: 'activation30d', label: 'Ativação em 30 dias', type: 'metric', unit: 'pct', infoKey: OVERVIEW_KPI_INFO.activation30d,
    value: indicatorValue('activation30d'),
  },
  {
    key: 'retentionW4', label: 'Retenção W4 (usuários)', type: 'metric', unit: 'pct', infoKey: OVERVIEW_KPI_INFO.retentionW4,
    value: indicatorValue('retentionW4'),
  },
  {
    key: 'top10Concentration', label: 'Concentração top 10 (MAU)', type: 'metric', unit: 'pct', infoKey: OVERVIEW_KPI_INFO.top10Concentration,
    value: indicatorValue('top10Concentration'),
  },
]);

export const kpiInfoKey = (key) => (Object.prototype.hasOwnProperty.call(OVERVIEW_KPI_INFO, key) ? OVERVIEW_KPI_INFO[key] : '');

export const kpiExportColumns = (kpis) => {
  const present = listOf(kpis).map((kpi) => textOf(kpi.key));
  return OVERVIEW_KPI_COLUMNS.filter((column) => column.key === 'period' || present.indexOf(column.key) !== -1);
};

export const OVERVIEW_ATTENTION_COLUMNS = defineColumns([
  { key: 'title', label: 'Insight', type: 'text', infoKey: 'overview.insight', width: 40 },
  { key: 'status', label: 'Situação', type: 'text', infoKey: 'overview.insightStatus', value: (row) => insightStatusLabel(objectOf(row).status) },
  { key: 'observation', label: 'Observação', type: 'text', infoKey: 'overview.insightObservation', width: 60 },
  { key: 'hypothesis', label: 'Hipótese', type: 'text', infoKey: 'overview.insightHypothesis', width: 40 },
  { key: 'nextStep', label: 'Próxima investigação', type: 'text', infoKey: 'overview.insightNextStep', width: 40 },
  { key: 'ruleKey', label: 'Regra', type: 'text', infoKey: 'overview.insightRule', screen: false },
  { key: 'periodFrom', label: 'Início do período', type: 'date', infoKey: 'overview.insightPeriod' },
  { key: 'periodTo', label: 'Fim do período', type: 'date', infoKey: 'overview.insightPeriod' },
  { key: 'sampleSize', label: 'Amostra', type: 'int', infoKey: 'overview.insightSample' },
  { key: 'confidence', label: 'Confiança', type: 'text', infoKey: 'overview.insightConfidence', value: (row) => confidenceLabel(objectOf(row).confidence) },
  { key: 'impactedUsers', label: 'Usuários impactados', type: 'int', infoKey: 'overview.insightImpactedUsers' },
  { key: 'impactedCompanies', label: 'Empresas impactadas', type: 'int', infoKey: 'overview.insightImpactedCompanies' },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  { key: 'generatedAtUtc', label: 'Gerado em', type: 'datetime', infoKey: 'overview.insightGeneratedAt', screen: false },
]);

export const concentrationRows = (companies) => listOf(companies).map((company, index) => {
  const share = isNumber(company.share) ? company.share : null;
  const mau = isNumber(company.mau) ? company.mau : null;
  return {
    key: textOf(company.shardCode) + ':' + (textOf(company.companyId) || String(index)),
    rank: index + 1,
    rankText: formatInteger(index + 1) + 'º',
    companyId: textOf(company.companyId),
    shardCode: textOf(company.shardCode),
    name: textOf(company.name) || textOf(company.companyId) || 'Empresa',
    share: share,
    shareText: share === null ? EMPTY : formatPercent(share),
    barPct: share === null ? null : Math.max(0, Math.min(100, share)),
    mau: mau,
    mauText: mau === null ? EMPTY : formatInteger(mau),
  };
});

export const OVERVIEW_CONCENTRATION_COLUMNS = defineColumns([
  { key: 'rank', label: '#', type: 'int', infoKey: 'overview.concentrationRank' },
  { key: 'name', label: 'Empresa', type: 'text', infoKey: 'common.company', width: 40 },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  { key: 'share', label: 'Participação no MAU', type: 'percent', infoKey: 'overview.concentrationShare' },
  { key: 'mau', label: 'MAU (30 dias)', type: 'int', infoKey: 'overview.companyMau' },
]);

const shownRatio = (row, field) => {
  const metric = guardedMetric(objectOf(row).successRate);
  return showsValue(metric) && isNumber(metric.numerator) && isNumber(metric.denominator) ? metric[field] : null;
};

export const OVERVIEW_OPERATION_COLUMNS = defineColumns([
  { key: 'label', label: 'Operação', type: 'text', infoKey: 'overview.operation', value: (row) => textOf(row.label) || textOf(row.operationKey) },
  { key: 'successRate', label: 'Sucesso', type: 'metric', unit: 'pct', infoKey: 'overview.operationSuccess' },
  { key: 'successes', label: 'Sucessos', type: 'int', infoKey: 'overview.operationSuccesses', screen: false, value: (row) => shownRatio(row, 'numerator') },
  { key: 'attempts', label: 'Tentativas', type: 'int', infoKey: 'overview.operationAttempts', screen: false, value: (row) => shownRatio(row, 'denominator') },
  { key: 'state', label: 'Estado', type: 'text', infoKey: 'overview.operationState', export: false, exportedAs: ['successRate', 'reason'] },
  {
    key: 'reason', label: 'Motivo do estado', type: 'text', infoKey: 'overview.operationState', screen: false,
    value: (row) => guardedMetric(objectOf(row).successRate).stateReason || '',
  },
]);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piOverviewModel.js